import React, { useState, useEffect, useMemo } from 'react';
import { Button } from '~/components/Button';
import { EditorInput } from '~/components/Controls/Editor';
import { BreadcrumbsResourcePage } from '~/components/ResourcePage';
import { VerticalTabs, VerticalTabsLayout } from '~/components/VerticalTabs';
import { ResourceLoader, controllableUseResource } from '~/lib/loader';
import { useAbility } from '~/lib/ability';
import { AppAdsAbility } from '~/modules/AppAds/ability';
import { fetchSellerDomains, fetchDistributionLinesByDomain, fetchDistributionLinesByPublisher } from '~/modules/AppAds/api';
import { AppAdsNavigation } from '~/modules/AppAds/components/AppAdsNavigation';
import { useSelectedPublisher } from '~/modules/AppAds/components/SelectedPublisherContext';
import { ILine } from '~/modules/Clients/models/Line';
import { downloadTxt } from '~/lib/file';
import { Intent } from '~/lib/intent';
import { ID } from '~/lib/model';
import { useCopyToClipboard } from '~/lib/useCopyToClipboard';
import { useFocusOnMount } from '~/lib/useFocusOnMount';
import { SelectSource } from '~/modules/Bidders/components/SelectSource';
import style from './index.module.scss';

export const AdminAppAdsView = () => {
  const ability = useAbility(AppAdsAbility);
  const isPublisherMode = ability.isSeller() || ability.isSelfServe();
  const sellerId = ability.sellerId && ability.sellerId();

  const { selectedPublisherId, setSelectedPublisherId } = useSelectedPublisher();
  const [activeDomain, setActiveDomain] = useState<string | undefined>(undefined);
  const [copied, copy] = useCopyToClipboard();
  const contentRef = useFocusOnMount<HTMLDivElement>(isPublisherMode);

  const publisherId = isPublisherMode ? sellerId : selectedPublisherId;

  const domainsResult = controllableUseResource(() => {
    if (publisherId) {
      return fetchSellerDomains(publisherId);
    }
    return Promise.resolve([]);
  }, []);

  const linesResult = controllableUseResource<ILine[]>(() => {
    if (!publisherId) return Promise.resolve([]);

    if (activeDomain) {
      return fetchDistributionLinesByDomain(activeDomain, publisherId);
    } else {
      return fetchDistributionLinesByPublisher(publisherId);
    }
  }, []);

  useEffect(() => {
    if (publisherId) {
      domainsResult.fetch();
    }
  }, [publisherId]);

  useEffect(() => {
    if (publisherId) {
      linesResult.fetch();
    }
  }, [publisherId, activeDomain]);

  const buildAppAdsText = (lines: ILine[]) =>
    lines
      .map((l) =>
        [l.domain, l.externalSellerId, l.sellerType, l.tagId]
          .filter(Boolean)
          .join(',')
      )
      .join('\n');

  const domainTabs = useMemo(() => {
    return [
      { key: 'common', id: undefined, title: 'Common' },
      ...(domainsResult.resource || []).map((domain) => ({
        key: domain,
        id: domain,
        title: domain,
        tooltip: domain,
      })),
    ];
  }, [domainsResult.resource]);

  const handleDownload = async () => {
    let content = '';
    if (linesResult.resource) {
      content = buildAppAdsText(linesResult.resource);
    }

    const filename = activeDomain
      ? `app-ads-${activeDomain}.txt`
      : `app-ads-${selectedPublisherId || sellerId}.txt`;

    downloadTxt(filename, content);
  };

  return (
    <ResourceLoader result={domainsResult} message="Loading domains">
      {({ resource: domains, error: domainsError }) => (
        <BreadcrumbsResourcePage
          title="Domains"
          toolbar={
            <div className={style.toolbar}>
              {!isPublisherMode && (
                <SelectSource
                  placeholder="Select seller"
                  searchable
                  combobox
                  autoFocus
                  className={style.publisherSelect}
                  value={selectedPublisherId}
                  onChange={(value) => {
                    setSelectedPublisherId(value as ID);
                    setActiveDomain(undefined);
                  }}
                />
              )}

              <Button
                responsive
                intent={Intent.DEFAULT}
                disabled={(!isPublisherMode && selectedPublisherId == null) || !domains?.length}
                onClick={handleDownload}
                leftIcon={<i className="fa fa-download" />}
              >
                Download
              </Button>
            </div>
          }
        >
          <AppAdsNavigation />
          <div className={`layout-content-box ${style.contentFocusable}`} ref={contentRef} tabIndex={-1}>
            {!isPublisherMode && selectedPublisherId == null ? (
              <div className={style.placeholderNote}>Select seller to display domains</div>
            ) : domainsError ? (
              <div className={style.errorNote}>
                Could not load domains.
                <Button intent={Intent.DEFAULT} onClick={() => domainsResult.fetch()}>
                  Retry
                </Button>
              </div>
            ) : !domains?.length ? (
              <div className={style.placeholderNote}>No domains available for this publisher</div>
            ) : (
              <VerticalTabsLayout
                sidebar={
                  <VerticalTabs
                    items={domainTabs}
                    activeId={activeDomain}
                    onChange={setActiveDomain}
                    autoFocusKey={isPublisherMode ? undefined : selectedPublisherId}
                  />
                }
              >
                <ResourceLoader
                  result={linesResult}
                  message={activeDomain ? `Loading lines for ${activeDomain}` : 'Loading all lines'}
                >
                  {({ resource: lines }) => {
                    const text = lines ? buildAppAdsText(lines) : '';
                    return (
                      <div className={style.missingLinesContent}>
                        <div className={style.missingLinesEditor}>
                          <EditorInput
                            mode="text"
                            minLines={5}
                            maxLines={30}
                            disabled
                            value={text}
                          />
                        </div>
                        {text && (
                          <div className={style.missingLinesActions}>
                            <Button
                              intent={copied ? Intent.SUCCESS : Intent.PRIMARY}
                              leftIcon={<i className={`fa fa-${copied ? 'check' : 'copy'}`} />}
                              onClick={() => copy(text)}
                            >
                              {copied ? 'Copied' : 'Copy'}
                            </Button>
                          </div>
                        )}
                      </div>
                    );
                  }}
                </ResourceLoader>
              </VerticalTabsLayout>
            )}
          </div>
        </BreadcrumbsResourcePage>
      )}
    </ResourceLoader>
  );
};
